import {ProteinSpecifier} from 'analyses/protein-alignment/util/ProteinAlignmentConstants';
import * as analytics from 'analytics/Analytics';
import {ProteinAlignment} from 'constants/AlignmentConstants';
import * as EventConstants from 'constants/EventConstants';
import * as MapsConstants from 'constants/MapsConstants';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import FileState from 'filestate/FileState';
import FileStates from 'FileStates';
import AppDispatcher from 'lib/Dispatcher';
import ProteinAlignmentTabState from 'molbio/tabstates/ProteinAlignmentTabState';
import notifications from 'notifications';
import * as TrayTaskActions from 'tray/TrayTaskActions';
import * as TrayTaskConstants from 'tray/TrayTaskConstants';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import {Optional} from 'util/Types';

import ProteinAlignmentDAO from './ProteinAlignmentDAO';

const ProteinAlignmentActions = {
  /**
   * Manage the opening of a tab to display the alignment, with error handling and loading state.
   * Get the full alignment dictionary from the server and dispatch an action to add it to the store.
   */
  open({proteinId, alignmentId}: {proteinId: string; alignmentId: string}): void {
    FileStates.get()
      .openFolderItem(proteinId, {open: true})
      .done((fileState) => {
        const existingTab = fileState!.findTab(
          ProteinAlignmentTabState,
          (tab) => tab.get('alignmentId') === alignmentId
        );
        if (existingTab) {
          existingTab.set('active', true);
        } else {
          LoadingScreen.showFileLoading();
          ProteinAlignmentDAO.get(alignmentId)
            .done((response) => {
              AppDispatcher.dispatch({
                actionType: EventConstants.PROTEIN_ALIGNMENT_OPEN_COMPLETED,
                alignment: response,
              });
              this._openAlignmentTab(fileState!, proteinId, alignmentId);
            })
            .fail(notifications.notifyError)
            .always(() => {
              LoadingScreen.hideFileLoading();
            });
        }
      });
  },

  _openAlignmentTab(fileState: FileState, proteinId: string, alignmentId: string): void {
    const newTab = new ProteinAlignmentTabState({proteinId, alignmentId});
    fileState.buffers().addTab(newTab, {side: 'left'});
    fileState.setIsWorkspaceSplit(false);
  },

  _handleOpenNewAlignment(proteinId: string, alignmentId: string): void {
    FileStates.get()
      .openFolderItem(proteinId, {open: true})
      .done((fileState) => {
        this._openAlignmentTab(fileState!, proteinId, alignmentId);
      });
  },

  _closeAlignmentTab(proteinId: string, alignmentId: string): void {
    const fileState = FileStates.get().get(proteinId);
    if (fileState) {
      const existingTab = fileState.findTab(
        ProteinAlignmentTabState,
        (tab) => tab.get('alignmentId') === alignmentId
      );
      if (existingTab) {
        existingTab.close();
      }
    }
  },

  create({
    proteinName,
    folderId,
    templateProteinIndex,
    proteinSpecifiers,
  }: {
    proteinName: string;
    folderId?: string;
    templateProteinIndex?: Optional<number>;
    proteinSpecifiers: Array<ProteinSpecifier>;
  }): void {
    const trayTaskId = TrayTaskActions.createTrayTask({
      heading: 'Your alignment is in progress. We will notify you when it completes.',
    });
    TrayTaskActions.onProgressFor(trayTaskId)(TrayTaskConstants.INDETERMINATE_PROGRESS);
    const isTemplateAlignment = templateProteinIndex != null;
    ProteinAlignmentDAO.create({proteinName, folderId, templateProteinIndex, proteinSpecifiers})
      .done((response) => {
        FolderDAOCache.invalidateFolderContents(folderId);
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTEIN_ALIGNMENT_OPEN_COMPLETED,
          alignment: response!.alignment,
        });
        TrayTaskActions.updateTrayTask(trayTaskId, {
          progress: TrayTaskConstants.SUCCESS_PROGRESS,
          heading: 'Successfully created alignment',
          onHeadingClick: () => {
            this._handleOpenNewAlignment(response!.protein.id, response!.alignment.id);
          },
        });
        analytics.track('protein_alignment', 'create_succeeded', {
          isTemplateAlignment,
          numProteins: proteinSpecifiers.length,
          length: response!.alignment.rows[0].proteinVersion.aminoAcids.length,
        });
      })
      .fail((response) => {
        const friendlyError = notifications.getFriendlyError(response);
        TrayTaskActions.updateTrayTask(trayTaskId, {
          progress: TrayTaskConstants.FAILURE_PROGRESS,
          error: friendlyError || 'Sorry, an unexpected error occurred.',
        });
        analytics.track('protein_alignment', 'create_failed', {
          isTemplateAlignment,
          numProteins: proteinSpecifiers.length,
          response,
        });
      });
  },

  /**
   * Recompute the given alignment with the given specifiers. If the alignment is a template
   * alignment, the template is implicitly preserved, so the specifiers should specify all
   * non-template proteins.
   */
  realign({
    alignment,
    proteinSpecifiers,
  }: {
    alignment: ProteinAlignment;
    proteinSpecifiers: Array<ProteinSpecifier>;
  }) {
    this._closeAlignmentTab(alignment.proteinId, alignment.id);
    const fileState = FileStates.get().get(alignment.proteinId);
    LoadingScreen.showFileLoading();

    const isTemplateAlignment = alignment.alignmentType === MapsConstants.ALIGNMENT_TYPE.TEMPLATE;

    let deferred;
    if (isTemplateAlignment) {
      deferred = ProteinAlignmentDAO.realignTemplate({alignmentId: alignment.id, proteinSpecifiers});
    } else {
      deferred = ProteinAlignmentDAO.realignConsensus({alignmentId: alignment.id, proteinSpecifiers});
    }
    deferred
      .done((response) => {
        analytics.track('protein_alignment', 'realign_succeeded', {
          isTemplateAlignment,
          numProteins: isTemplateAlignment ? proteinSpecifiers.length + 1 : proteinSpecifiers.length,
          length: response!.alignment.rows[0].proteinVersion.aminoAcids.length,
        });
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTEIN_ALIGNMENT_OPEN_COMPLETED,
          alignment: response!.alignment,
        });
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTEIN_OPEN_COMPLETED,
          protein: response!.protein,
          proteinId: response!.protein.id,
        });
      })
      .fail((response) => {
        notifications.notifyError(response);
        analytics.track('protein_alignment', 'realign_failed', {
          isTemplateAlignment,
          numProteins: isTemplateAlignment ? proteinSpecifiers.length + 1 : proteinSpecifiers.length,
          response,
        });
      })
      .always(() => {
        if (fileState) {
          this._openAlignmentTab(fileState, alignment.proteinId, alignment.id);
        }
        LoadingScreen.hideFileLoading();
      });
  },

  delete(proteinId: string, alignmentId: string): void {
    ProteinAlignmentDAO.delete(alignmentId)
      .done((response) => {
        this._closeAlignmentTab(proteinId, alignmentId);
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTEIN_ALIGNMENT_DELETE_COMPLETED,
          alignmentId,
        });
      })
      .fail(notifications.notifyError);
  },

  rename(alignmentId: string, name: string): void {
    if (name.length > 255) {
      notifications.info('Protein alignment name will be truncated at 255 characters.');
    }
    ProteinAlignmentDAO.rename(alignmentId, name)
      .done((response) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTEIN_ALIGNMENT_RENAME_COMPLETED,
          alignmentId,
          name: name.slice(0, 255),
        });
      })
      .fail(notifications.notifyError);
  },
};

export default ProteinAlignmentActions;
